import { collection, getDocs, limit, query } from "firebase/firestore";

const SCHEMA_CACHE = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000;

function inferType(value) {
  if (value == null) return "null";
  if (typeof value?.toDate === "function") return "timestamp";
  if (value instanceof Date) return "timestamp";
  if (Array.isArray(value)) return "array";
  if (typeof value === "object") return "object";
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return "number";
  return "string";
}

function scanDoc(data, schema, prefix = "") {
  Object.entries(data || {}).forEach(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (!schema[path]) schema[path] = { types: {}, count: 0, optional: true };
    const type = inferType(value);
    schema[path].types[type] = (schema[path].types[type] || 0) + 1;
    schema[path].count += 1;
    if (value != null && value !== "") schema[path].optional = false;
    if (type === "object" && value && !value.toDate) scanDoc(value, schema, path);
    if (type === "array" && Array.isArray(value) && value[0] && typeof value[0] === "object") {
      scanDoc(value[0], schema, `${path}[]`);
    }
  });
}

/**
 * Scan a collection sample and build a schema map. Results are cached in-memory.
 */
export async function analyzeCollectionSchema(firestore, collectionName, sampleSize = 50) {
  const cacheKey = `${collectionName}:${sampleSize}`;
  const cached = SCHEMA_CACHE.get(cacheKey);
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) return cached.schema;

  const snap = await getDocs(query(collection(firestore, collectionName), limit(sampleSize)));
  const fieldSchema = {};
  snap.docs.forEach((d) => scanDoc(d.data(), fieldSchema));

  const fields = Object.entries(fieldSchema).map(([path, info]) => {
    const dominantType = Object.entries(info.types).sort((a, b) => b[1] - a[1])[0]?.[0] || "unknown";
    return {
      path,
      type: dominantType,
      types: info.types,
      sampleCount: info.count,
      optional: info.optional,
      required: !info.optional,
    };
  }).sort((a, b) => a.path.localeCompare(b.path));

  const schema = {
    collection: collectionName,
    documentCount: snap.size,
    fields,
    relationships: detectRelationships(fields),
    nestedObjects: fields.filter((f) => f.type === "object").map((f) => f.path),
    arrays: fields.filter((f) => f.type === "array").map((f) => f.path),
  };

  SCHEMA_CACHE.set(cacheKey, { schema, at: Date.now() });
  return schema;
}

function detectRelationships(fields) {
  const refs = fields.filter((f) =>
    f.path.endsWith("Id") || f.path.endsWith("Ref") || f.type === "reference"
    || /userId|projectId|employeeId|connectionId/i.test(f.path)
  );
  return refs.map((f) => ({
    field: f.path,
    likelyTarget: f.path.replace(/Id$/, "s").replace(/Ref$/, ""),
    confidence: f.path.endsWith("Id") ? "medium" : "low",
  }));
}

export function clearSchemaCache() {
  SCHEMA_CACHE.clear();
}
